/**
 * CollectionFilter
 *
 * Manages client-side filtering, pagination, and facet adjustment
 * for the PLP (Product Listing Page). Consumes configuration from
 * a <script type="application/json" id="collection-filter-config"> block
 * rendered by Liquid.
 */
class CollectionFilter {
  constructor() {
    const configEl = document.getElementById("collection-filter-config");
    if (!configEl) {
      console.warn("CollectionFilter: No config element found.");
      return;
    }

    this.config = JSON.parse(configEl.textContent);
    this.itemsPerPage =
      this.config.productsPerPage - this.config.inGridCount * 2;
    this.adjustedItemsPerPage = this.config.productsPerPage;

    this.state = {
      selectedColors: [],
      selectedSizes: [],
      lowerPrice: null,
      upperPrice: null,
      specialFilter: "",
      activeCards: [],
      activeCardsOverflow: [],
      totalVisibleCards: 0,
      previousUrl: window.location.href,
      moreCardsShown: false,
      hasMoreServerPages: false,
      nextPageUrl: null,
    };

    this.availableOptions = this.buildAvailableOptions();
    this.groupedColorMap = this.buildGroupedColorMap();

    this.container = document.querySelector(".product-grid-container");
    this.spinner = document.querySelector(".wholepage-loading-spinner");
    this.mobileFilterDrawer = document.querySelector(
      "details.mobile-facets__disclosure",
    );

    this.init();
  }

  // --- Initialization ---

  init() {
    this.showSpinner();

    // Initialize server pagination state from DOM
    this.initServerPaginationState();

    this.handleProductCards({
      showMore: false,
      activeItems: 0,
      moreCardsShown: false,
    });

    this.adjustFilters();
    this.removeSpecialFilter();
    this.addSpinnerOnFilterClick();
    this.updateLoadMoreVisibility();
    this.container?.classList.remove("hidden");
    this.closeDrawer();
    this.hideSpinner();
    this.observeGridChanges();
  }

  initServerPaginationState() {
    const button = document.querySelector("#load-more button");
    const url = button?.dataset?.url;
    this.state.hasMoreServerPages = !!(url && url !== "");
    this.state.nextPageUrl = url || null;
  }

  observeGridChanges() {
    const target = document.querySelector(".product-grid-container");
    if (!target) return;

    let initialized = false;

    const observer = new MutationObserver((mutations) => {
      if (!initialized) return;

      for (const mutation of mutations) {
        if (
          mutation.type === "childList" &&
          mutation.target.id === "ProductGridContainer"
        ) {
          this.state.activeCardsOverflow = [];
          this.addSpinnerOnFilterClick();
          this.container?.classList.add("hidden");

          // Re-initialize server pagination state after grid re-render
          this.initServerPaginationState();

          this.handleProductCards({
            showMore: false,
            activeItems: 0,
            moreCardsShown: false,
          });

          this.adjustFilters();
          this.removeSpecialFilter();
          this.updateLoadMoreVisibility();
          this.container?.classList.remove("hidden");
          this.closeDrawer();
          this.hideSpinner();
          break;
        }
      }
    });

    observer.observe(target, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    // Delay activation so we don't fire on initial page load mutations
    requestAnimationFrame(() => {
      initialized = true;
    });
  }

  // --- Data builders ---

  buildAvailableOptions() {
    const options = {};
    for (const [color, size] of this.config.availableVariants) {
      if (!options[color]) {
        options[color] = [size];
      } else if (!options[color].includes(size)) {
        options[color].push(size);
      }
    }
    return options;
  }

  buildGroupedColorMap() {
    const map = {};
    for (const [name, values] of Object.entries(this.config.groupedColors)) {
      map[name] = values.map((v) => v.trim());
    }
    return map;
  }

  // --- URL / Filter Parsing ---

  parseFiltersFromUrl() {
    const url = window.location.href;
    const params = url.split("&");
    const encodedColor = encodeURI(
      (this.config.colorOptionName || "").replace(" ", "+"),
    );
    const encodedSize = encodeURI(
      (this.config.sizeOptionName || "").replace(" ", "+"),
    );

    const colors = [];
    const sizes = [];
    let lowerPrice = null;
    let upperPrice = null;
    let specialFilter = "";

    for (const param of params) {
      if (param.includes(encodedColor)) {
        const val = param.split("=")[1];
        colors.push(
          decodeURIComponent(val).replaceAll("+", " ").toLowerCase().trim(),
        );
      }
      if (param.includes(encodedSize)) {
        const val = param.split("=")[1];
        sizes.push(
          decodeURIComponent(val).replaceAll("+", " ").toLowerCase().trim(),
        );
      }
      if (param.includes("price.gte")) {
        lowerPrice = parseInt(param.split("=")[1], 10);
      }
      if (param.includes("price.lte")) {
        upperPrice = parseInt(param.split("=")[1], 10);
      }
      if (param.includes("filter.v.m.custom.special_filter")) {
        specialFilter = decodeURIComponent(param.split("=")[1])
          .toLowerCase()
          .trim();
      }
    }

    this.state.selectedColors = colors;
    this.state.selectedSizes = sizes;
    this.state.lowerPrice = lowerPrice;
    this.state.upperPrice = upperPrice;
    this.state.specialFilter = specialFilter;
  }

  // --- Card Filtering ---

  shouldHideCard(card) {
    const variantAvailable = card.dataset.available;
    const productAvailable = card.dataset.productavailable;

    if (!this.config.showOutOfStock && variantAvailable === "false") {
      return true;
    }
    if (variantAvailable === "false" && productAvailable === "true") {
      return true;
    }
    if (card.dataset.applyMerchandisingRule === "true") {
      return true;
    }
    return false;
  }

  matchesFilters(card) {
    const filterColor = card.dataset.filtercolor.toLowerCase().trim();
    const filterSize = card.dataset.filtersize.toLowerCase();
    const filterMatched = card.dataset.filterMatched;
    const filterPrice = parseInt(card.dataset.price, 10) / 100;

    const {
      selectedColors,
      selectedSizes,
      lowerPrice,
      upperPrice,
      specialFilter,
    } = this.state;

    // Size filter
    if (selectedSizes.length > 0 && !selectedSizes.includes(filterSize)) {
      return { visible: false };
    }

    // Price filter
    if (
      (upperPrice || lowerPrice) &&
      (filterPrice > upperPrice || filterPrice < lowerPrice)
    ) {
      return { visible: false };
    }

    // Grouped color match
    let matchesGroupedColor = Object.keys(this.groupedColorMap).length > 0;
    if (Object.keys(this.groupedColorMap).length) {
      for (const selectedColor of selectedColors) {
        if (this.groupedColorMap[selectedColor]?.includes(filterColor)) {
          matchesGroupedColor = true;
          break;
        }
      }
    }

    const selectedColorMatch = filterMatched == "true" ? true : false;

    // Color filter (if not matched by grouped color)
    if (
      !matchesGroupedColor &&
      !selectedColorMatch &&
      !window.themeSettings.separateProductsByColorVariant
    ) {
      return { visible: false };
    }

    // Special filter
    if (specialFilter && card.dataset.specialFilter !== specialFilter) {
      return { visible: false };
    }

    return { visible: true, filterColor, matchesGroupedColor };
  }

  handleProductCards({
    productCards = null,
    showMore = false,
    activeItems = 0,
    moreCardsShown = false,
  }) {
    if (window.location.href !== this.state.previousUrl) {
      this.state.totalVisibleCards = 0;
      this.state.previousUrl = window.location.href;
    }

    this.parseFiltersFromUrl();

    const cards = productCards || document.querySelectorAll("li.grid__item");
    const itemsToRender = moreCardsShown
      ? this.adjustedItemsPerPage
      : this.itemsPerPage;
    const uniqueTitleColor = new Set();
    let actualActiveItems = activeItems;
    const activeCards = [];
    cards.forEach((card) => {
      card.classList.remove("hidden");
      if (this.shouldHideCard(card)) {
        card.classList.add("hidden");
        return;
      }

      const result = this.matchesFilters(card);
      if (!result.visible) {
        card.classList.add("hidden");
        return;
      }

      const title = card.dataset.title.toLowerCase().trim();
      const filterColor =
        result.filterColor || card.dataset.filtercolor.toLowerCase().trim();
      const uniqueKey = `${title}-${filterColor}`;

      // Deduplicate by title+color
      if (uniqueTitleColor.has(uniqueKey)) {
        card.classList.add("hidden");
        return;
      }
      uniqueTitleColor.add(uniqueKey);

      // Paginate
      if (actualActiveItems < itemsToRender) {
        activeCards.push(card);
        actualActiveItems++;
      } else {
        this.state.activeCardsOverflow.push(card);
        card.classList.add("hidden");
      }
    });

    // Post-filter: enforce size match on visible cards
    // Handles cases where Shopify's server-side re-render returns a card
    // with a different size variant than the one selected
    if (this.state.selectedSizes.length > 0) {
      for (let i = activeCards.length - 1; i >= 0; i--) {
        const card = activeCards[i];
        const cardSize = card.dataset.filtersize.toLowerCase().trim();
        if (!this.state.selectedSizes.includes(cardSize)) {
          card.classList.add("hidden");
          activeCards.splice(i, 1);
          actualActiveItems--;
        }
      }
    }

    this.state.totalVisibleCards += actualActiveItems;

    // Show "no results" if nothing visible
    if (actualActiveItems === 0 && !showMore) {
      const noResult = document.querySelector(".collection--empty");
      noResult?.classList.remove("hidden");
    }

    // Append cards for load-more
    if (showMore && activeCards.length) {
      const grid = document.querySelector("ul#product-grid");
      if (grid) {
        for (const card of [
          ...activeCards,
          ...this.state.activeCardsOverflow,
        ]) {
          grid.appendChild(card);
        }
      }
    }

    // Hide ingrid elements if not enough products
    if (this.config.isCollectionTemplate) {
      this.adjustIngridElements();
    }

    this.state.activeCards = activeCards;
    return { activeCards, actualActiveItems };
  }

  adjustIngridElements() {
    const ingridElements = document.querySelectorAll(".ingrid-element");
    const ingridCount = ingridElements.length;

    ingridElements.forEach((el) => {
      const line = parseInt(el.dataset.line, 10);
      const threshold = line * 4 - ingridCount * 2;
      if (this.state.totalVisibleCards <= threshold) {
        el.classList.add("hidden");
      }
    });
  }

  // --- Filter Facet Adjustment ---

  adjustFilters() {
    const facets = document.querySelectorAll(
      "li.list-menu__item > label > input",
    );

    facets.forEach((facet) => {
      if (!facet.hasAttribute("value")) return;

      // Remove parenthetical counts from label text
      const labelSpan = this.findNextSibling(facet, "SPAN");
      if (!labelSpan) return;

      const text = labelSpan.innerHTML;
      const parenIndex = text.indexOf("(");
      if (parenIndex !== -1) {
        labelSpan.innerText = text.slice(0, parenIndex);
      }

      const facetWrapper = facet.closest(".list-menu__item");
      const detailsWrapper = facet.closest("details");
      if (!detailsWrapper) return;

      const filterName = detailsWrapper.dataset.filterName || "";
      const isColor = filterName.includes(this.config.colorOptionName || "");
      const isSize = filterName.includes(this.config.sizeOptionName || "");
      const isTag = filterName.includes(this.config.tagOptionName || "");
      const facetValue = facet.getAttribute("value").toLowerCase();

      let shouldDisplay = true;

      if (isColor) {
        shouldDisplay = this.shouldDisplayColorFacet(facetValue);
      } else if (isSize) {
        shouldDisplay = this.shouldDisplaySizeFacet(facetValue);
      } else if (isTag) {
        shouldDisplay = this.shouldDisplayTagFacet(facet);
      }

      facetWrapper?.classList.toggle("hidden", !shouldDisplay);
    });

    // Restore price filter UI state
    const urlParams = new URLSearchParams(window.location.search);
    const lteValue = urlParams.get("filter.v.price.lte");
    if (lteValue && !isNaN(lteValue) && Number(lteValue) > 0) {
      const priceEl = document.querySelector(
        `[data-max-value='${parseInt(lteValue, 10)}']`,
      );
      priceEl?.classList.add("checked");
    }
  }

  shouldDisplayColorFacet(value) {
    const hasAvailable = Object.keys(this.availableOptions).includes(value);
    const hasGrouped = Object.keys(this.groupedColorMap).includes(value);
    if (!(hasAvailable || hasGrouped) && location.href.includes("filter")) {
      return false;
    }
    return true;
  }

  shouldDisplaySizeFacet(value) {
    const { selectedColors } = this.state;

    if (selectedColors.length > 0) {
      for (const color of selectedColors) {
        const actualColors = this.groupedColorMap[color] || [color];
        for (const actualColor of actualColors) {
          if (this.availableOptions[actualColor]?.includes(value)) {
            return true;
          }
        }
      }
      return false;
    }

    // No color selected — show if any color has this size
    return Object.values(this.availableOptions).some((sizes) =>
      sizes.includes(value),
    );
  }

  shouldDisplayTagFacet(facet) {
    const prefix = this.config.tagFilterSubstring;

    if (prefix) {
      if (facet.value.includes(prefix)) {
        const labelSpan = this.findNextSibling(facet, "SPAN");
        if (labelSpan) {
          labelSpan.innerText = facet.value.replace(prefix, "");
        }
      } else {
        return false;
      }
    }

    const label = facet.closest("label");
    if (label?.classList.contains("facet-checkbox--disabled")) {
      return false;
    }

    return true;
  }

  // --- Special Filter ---

  removeSpecialFilter() {
    const handle = this.config.specialCollectionHandle;
    if (handle && !location.href.includes(handle)) {
      document.querySelectorAll(".special_filter-class").forEach((el) => {
        el.classList.add("hidden");
      });
    }
  }

  // --- Load More ---

  loadMoreProducts() {
    const hasOverflow = this.state.activeCardsOverflow.length > 0;
    const hasMoreServerPages = this.state.hasMoreServerPages;

    if (!hasOverflow && !hasMoreServerPages) {
      return;
    }

    this.showSpinner();

    // First, drain overflow cards
    if (hasOverflow) {
      const numToRender = this.adjustedItemsPerPage;
      let shown = 0;

      for (
        let i = 0;
        i < numToRender && i < this.state.activeCardsOverflow.length;
        i++
      ) {
        this.state.activeCardsOverflow[i].classList.remove("hidden");
        shown++;
      }

      this.state.activeCardsOverflow.splice(0, shown);
      this.state.totalVisibleCards += shown;

      this.updateLoadMoreVisibility();
      this.hideSpinner();
      document.dispatchEvent(new CustomEvent("finishedLoadPLP"));
      return;
    }

    // No overflow, fetch next page from server
    if (!this.state.nextPageUrl) {
      this.state.hasMoreServerPages = false;
      this.updateLoadMoreVisibility();
      this.hideSpinner();
      return;
    }

    fetch(this.state.nextPageUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const newCards = doc.querySelectorAll("li.grid__item");
        const newLoadMoreBtn = doc.querySelector("#load-more button");
        const newNextUrl = newLoadMoreBtn?.dataset?.url;

        // Update server pagination state
        this.state.hasMoreServerPages = !!(newNextUrl && newNextUrl !== "");
        this.state.nextPageUrl = newNextUrl || null;

        // Process the new cards
        this.handleProductCards({
          productCards: newCards,
          showMore: true,
          activeItems: 0,
          moreCardsShown: true,
        });

        this.updateLoadMoreVisibility();
        window.reinitializeCarousels();
        this.hideSpinner();
        document.dispatchEvent(new CustomEvent("finishedLoadPLP"));
      })
      .catch((err) => {
        console.error("CollectionFilter: Failed to load more products", err);
        this.hideSpinner();
      });
  }

  updateLoadMoreVisibility() {
    const loadMoreContainer = document.querySelector("#load-more");
    if (!loadMoreContainer) return;

    const hasOverflow = this.state.activeCardsOverflow.length > 0;
    const hasMoreServerPages = this.state.hasMoreServerPages;

    // Show button if there are overflow cards OR if server has more pages
    const shouldShow = hasOverflow || hasMoreServerPages;
    loadMoreContainer.classList.toggle("hidden", !shouldShow);
  }

  // --- UI Helpers ---

  showSpinner() {
    this.spinner?.classList.remove("hidden!");
    this.spinnerTimeout = setTimeout(() => {
      this.spinner?.classList.add("hidden!");
    }, 5000);
  }

  hideSpinner() {
    clearTimeout(this.spinnerTimeout);
    this.spinner?.classList.add("hidden!");
  }

  closeDrawer() {
    if (this.mobileFilterDrawer) {
      this.mobileFilterDrawer.closest("menu-drawer")?.closeMenuDrawer(true);
    }
  }

  addSpinnerOnFilterClick() {
    const options = document.querySelectorAll(
      ".fltr-option, facet-remove > a, #apply-price-filter-btn",
    );
    options.forEach((el) => {
      el.addEventListener("click", () => {
        const input = el.querySelector('input[type="checkbox"]');
        if (input?.disabled) return;
        this.showSpinner();
      });
    });
  }

  findNextSibling(el, tagName) {
    let sibling = el.nextElementSibling;
    while (sibling && sibling.tagName !== tagName) {
      sibling = sibling.nextElementSibling;
    }
    return sibling;
  }
}

// --- Global entry point for onclick handlers ---

function loadMoreProducts() {
  if (!window.collectionFilter) return;
  window.collectionFilter.loadMoreProducts();
}

document.addEventListener("DOMContentLoaded", () => {
  window.collectionFilter = new CollectionFilter();
});
